<template>
  <asd20-modal
    v-if="event"
    :open="open"
    :title="
      `${event.calendarOrganizationTitle || event.calendarLocation} Event`
    "
    icon="calendar-alt"
    dismissable
    class="asd20-event-modal"
    @dismiss="$emit('dismiss')"
    :style="{ '--accent-color': event.calendarColor }"
  >
    <Asd20Viewport tabindex="0" class="asd20-event-modal__details" scrollable>
      <header class="asd20-event-modal__header">
        <div class="asd20-event-modal__date">
          <div class="top">{{ event.weekday }}</div>
          <div class="middle">{{ event.day }}</div>
          <div class="bottom">{{ event.month }}</div>
          <small>{{ event.year }}</small>
        </div>
        <h2 class="name">{{ sanitizedSummary }}</h2>
      </header>
      <Asd20List class="description">
        <Asd20ListItem
          v-if="time !== null"
          icon="hours-alt"
          icon-size="lg"
          label="Time"
          :description="event.allDay ? 'All Day' : time"
        />
        <Asd20ListItem
          icon="map"
          icon-size="lg"
          v-if="event.location"
          label="Location"
          :description="event.location"
        />
        <Asd20ListItem
          v-if="sanitizedDescription"
          label="Description"
          :description="sanitizedDescription"
        />
      </Asd20List>
      <div class="tags">
        <Asd20Tag
          v-for="(category, index) in event.calendarCategories"
          :key="index"
          :label="category"
        />
        <Asd20Tag :label="event.calendarName" />
      </div>
    </Asd20Viewport>
  </asd20-modal>
</template>

<script>
import Asd20Modal from '../../molecules/Asd20Modal'
import Asd20Tag from '../../atoms/Asd20Tag'
import Asd20List from '../../organisms/Asd20List'
import Asd20ListItem from '../../molecules/Asd20ListItem'
import Asd20Viewport from '../../atoms/Asd20Viewport'
import formattedTime from '../../../helpers/formatEventTime'
import truncate from 'lodash-es/truncate'

export default {
  name: 'Asd20EventModal',
  components: { Asd20Modal, Asd20Tag, Asd20List, Asd20ListItem, Asd20Viewport },
  props: {
    event: { type: Object, default: null },
    open: { type: Boolean, default: false },
  },
  computed: {
    sanitizedSummary() {
      return this.sanitizeSummary(this.event.summary)
    },
    sanitizedDescription() {
      return this.sanitizeDescription(this.event.description)
    },
    title() {
      return truncate(this.sanitizedSummary, { length: 75 })
    },
    time() {
      return this.event ? formattedTime(this.event) : null
    },
  },
  methods: {
    // specifically for rSchool events that have extraneous school names in the summary
    sanitizeSummary(summary) {
      if (!summary) return summary
      const pattern = /vs\.[^>]*>Multiple Schools/
      return summary.replace(pattern, 'vs. Multiple Schools')
    },
    // Function to sanitize the description
    sanitizeDescription(description) {
      if (!description) return description

      // Check if the description contains ">Multiple Schools"
      if (description.includes('>Multiple Schools')) {
        // Remove text between the ">" backwards to the first "." or ":" and replace '>Multiple Schools' with '.'
        description = description.replace(
          /([.:][^>]*?)>([^>]*Multiple Schools)/,
          '$1.'
        )

        // Separate capital letters preceded by lowercase letters with ', '
        description = description.replace(/([a-z])([A-Z])/g, '$1, $2')

        // Remove unmatched quotes between "." or ":" and ">"
        const quotePattern = /[.:][^>]*['"][^'"]*$|[^'"]*['"][^>]*['"]>Multiple Schools/g
        description = description.replace(quotePattern, match => {
          const quotes = match.match(/['"]/g) || []
          return quotes.length % 2 !== 0 ? match.replace(/['"]/g, '') : match
        })
      }

      return description
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../design/_mixins.scss';
@import '../../../design/_variables.scss';

.asd20-event-modal {
  --accent-color: var(--color__secondary-t30);

  &__header {
    // background: var(--website-page__alternate-background-t50);
    // border-top: space(4.5) solid var(--color__accent);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: space(5);
      // var(--website-card__reverse-background-color)
      background-color: var(--color__secondary-t30);
      background-image: url('/statics/images/chalk.png');
      background-position-y: top;
      background-size: 640px auto;
    }
    .name {
      font-size: 1.25rem;
      color: var(--color__primary);
      text-align: center;
      margin: 0 space(2);
      line-height: 1.1;
      margin-bottom: 0;
    }
    .calendar-location {
      font-size: 1.25rem;
      color: var(--accent-color);
      text-align: center;
      margin: 0 space(2);
      line-height: 1.1;
      margin-bottom: 0;
    }
  }
  &__date {
    position: relative;
    z-index: 1;
    margin-top: space(1);
    background: var(--website-card__reverse-background-color)
      var(--website-card__reverse-background-style);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 8rem;
    height: 8rem;
    margin-bottom: space(1);
    border-radius: var(--website-shape__radius-s);
    small {
      color: var(---website-card__reverse-foreground-color);
    }
    .top,
    .bottom {
      color: var(--website-card__reverse-foreground-color);
      text-transform: uppercase;
      text-align: center;
      font-weight: bold;
      font-size: 0.75rem;
      line-height: 1;
      font-family: var(--website-typography__font-family-body);
    }
    .top {
      color: var(--color__tertiary);
    }
    .middle {
      // color: var(--color__accent-t100);
      color: var(--website-icon__line-color-reverse);
      text-transform: uppercase;
      font-weight: bold;
      font-size: 4rem;
      line-height: 1;
    }
  }
  &__details {
    .description,
    .tags {
      display: flex;
      max-width: 100%;
      padding: space(1);
      font-size: 1rem;

      * {
        align-items: center;
      }
      &::v-deep ul {
        margin-top: 0;
        padding-inline-start: 0;
      }
      &::v-deep li {
        margin-left: 1rem;
        font-size: 1rem !important;
      }
    }
  }
}
@media (min-width: 1024px) {
  .asd20-event-modal {
    &__header {
      &::before {
        height: space(7);
      }
      .name {
        font-size: 1.5rem;
      }
    }
    &::v-deep .asd20-modal {
      margin-top: auto;
      margin-bottom: auto;
      max-width: 40vw;
    }
    &__date {
      width: 12rem;
      height: 12rem;
      .top,
      .bottom {
        font-size: 1.125rem;
      }
      .middle {
        font-size: 6rem;
      }
    }
    &__details {
    }
  }
}
</style>
